import { Config, Transaction, WorkflowObjectState } from 'olympe';
import React, { useRef, useEffect, useState } from 'react';
import {
  DialogContent,
  Dialog,
  DialogTitle,
  DialogActions,
  Tooltip, Typography
} from '@mui/material';
import { getIcon } from '../shared/utils/_icons.jsx';
import CheckIcon from '@mui/icons-material/Check';
import Grid from '@mui/material/Grid';
import BCP from '../../common/section_bcp/DM_BCP';
import { getProps } from '../shared/_helpers';
import { updateProperty } from '../shared/datagrid/DataGridRenderers.jsx';
import { styled } from '@mui/material/styles';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import { getLogger } from '@olympeio/core';
import Alert from '@mui/material/Alert';
import InfoIcon from '@mui/icons-material/Info';
import Button from '@mui/material/Button';
import { QA_ROLES } from '../section_sf/Utils';
import {
  getCurrentWorkflow,
  BCP_WORKFLOW_STATES,
  workflowStatesForApplyButtonDisabled
} from '../../common/section_bcp/BcpWorkflowAuthorizations.js';
import {UserDropdown} from './renderers/UserDropdown.jsx';

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialogContent-root': {
    padding: theme.spacing(2),
    height: '420px',
  },
  '& .MuiDialogActions-root': {
    padding: theme.spacing(1),
  },
  '& .MuiDialogContent-root::-webkit-scrollbar': {
    display: 'masonry !important',
  },
}));

/**
 * @param {Object} props
 * @return {JSX.Element}
 */

export const EditQADialog = (props) => {
  let {
    context,
    bcpRequest,
    closePopup,
    setRefreshMe,
    refreshMe,
    refreshLine,
    apiRef,
  } = props;
  const dialogRef = useRef(null);
  const qualityAssuranceInFactory = bcpRequest.get(BCP.qaInFactoryProp);
  const technicalApplicationGroup = bcpRequest.get(
    BCP.technicalApplicationGroupProp
  );
  const [successMessage, setSuccessMessage] = useState(false)
  const qualityAssurance = bcpRequest.get(BCP.qualityAssuranceProp);
  const businessPartner = bcpRequest.get(BCP.businessPartnerProp);
  const procurementManager = bcpRequest.get(BCP.procurementManagerProp);
  const procurementSpecialist = bcpRequest.get(BCP.procurementSpecialistProp);
  const requestor = bcpRequest.get(BCP.requestorProp);

  // SQA link list - to refer the document
  const sqaLink = Config.getParameter('sqaListLink');

  let [ctag, setCTAG] = useState(technicalApplicationGroup);
  let [sqa, setSQA] = useState(qualityAssurance);
  let [qaInFactory, setQAFactory] = useState(qualityAssuranceInFactory);
  const [bp, setBp] = useState(businessPartner);
  const [pm, setPm] = useState(procurementManager);
  const [ps, setPs] = useState(procurementSpecialist);
  const [requestr, setRequestr] = useState(requestor);

  const currentWorkflowState = getCurrentWorkflow(bcpRequest, context)?.get(
    WorkflowObjectState.stateProp
  );



  const prevValues = useRef({ ctag, sqa, qaInFactory, bp, pm, ps });
  const dropdownFields = [
    {
      label: 'Requestor',
      labelText: 'Requestor can be updated by any stakeholder in the workflow or admins accordingly',
      column: 'Requestor',
      value: requestr,
      workflowStatesForDropdownDisable: [
        BCP_WORKFLOW_STATES.WAITING_ON_COMPLETION,
        BCP_WORKFLOW_STATES.COMPLETED,
        BCP_WORKFLOW_STATES.REJECTED,
      ],
    },
    {
      label: 'Business Partner',
      labelText: 'BP is responsible to allign Business Priority & resource allocation with Business unit & other functions. This is a critical step for the workflow',
      column: 'Business Partner',
      value: bp,
      workflowStatesForDropdownDisable: [
        BCP_WORKFLOW_STATES.ALT_VS_CHECK_BY_PM,
        BCP_WORKFLOW_STATES.WAITING_ON_COMPLETION,
        BCP_WORKFLOW_STATES.CTAG_ACCEPTANCE,
        BCP_WORKFLOW_STATES.QA_FACTORY_ACCEPTANCE_1,
        BCP_WORKFLOW_STATES.QA_FACTORY_ACCEPTANCE_2,
        BCP_WORKFLOW_STATES.COMPLETED,
        BCP_WORKFLOW_STATES.REJECTED,
      ],
    },
    {
      label: 'Procurement Manager',
      labelText: 'PM is responsible to finallize the alternative vendor to be developed as a BCP',
      column: 'Procurement Manager',
      value: pm,
      workflowStatesForDropdownDisable: [
        BCP_WORKFLOW_STATES.WAITING_ON_COMPLETION,
        BCP_WORKFLOW_STATES.CTAG_ACCEPTANCE,
        BCP_WORKFLOW_STATES.QA_FACTORY_ACCEPTANCE_1,
        BCP_WORKFLOW_STATES.QA_FACTORY_ACCEPTANCE_2,
        BCP_WORKFLOW_STATES.COMPLETED,
        BCP_WORKFLOW_STATES.REJECTED,
      ],
    },
    {
      label: 'Application Group',
      labelText: 'Application Group is resposible to provide a due date for the completion of local acceptence activities',
      column: 'CTAG/AG',
      value: ctag,
      workflowStatesForDropdownDisable: [
        BCP_WORKFLOW_STATES.WAITING_ON_COMPLETION,
        BCP_WORKFLOW_STATES.QA_FACTORY_ACCEPTANCE_1,
        BCP_WORKFLOW_STATES.QA_FACTORY_ACCEPTANCE_2,
        BCP_WORKFLOW_STATES.COMPLETED,
        BCP_WORKFLOW_STATES.REJECTED,
      ],
    },
    {
      label: 'Supplier Quality Assurance at Host Market',
      labelText: 'SQA at host market is to provide a due date for the completion of vendor audit related activities',
      column: 'SQA at host market',
      value: sqa,
      workflowStatesForDropdownDisable: [
        BCP_WORKFLOW_STATES.WAITING_ON_COMPLETION,
        BCP_WORKFLOW_STATES.COMPLETED,
        BCP_WORKFLOW_STATES.REJECTED,
      ],
    },
    {
      label: 'Quality in Factory',
      labelText: 'QA in factory is resposible to provide a due date for the completion of QA related activities & QIR creation ',
      column: 'QA in Factory',
      value: qaInFactory,
      workflowStatesForDropdownDisable: [
        BCP_WORKFLOW_STATES.WAITING_ON_COMPLETION,
        BCP_WORKFLOW_STATES.COMPLETED,
        BCP_WORKFLOW_STATES.REJECTED,
      ],
    },
    {
      label: 'Procurement Specialist',
      labelText: 'Inclusion of PS is optional. PS can support in maintaining/updating the inputs provided in the workflow',
      column: 'Procurement Specialist',
      value: ps,
      workflowStatesForDropdownDisable: [
        BCP_WORKFLOW_STATES.WAITING_ON_COMPLETION,
        BCP_WORKFLOW_STATES.COMPLETED,
        BCP_WORKFLOW_STATES.REJECTED,
      ],
    },
  ];
  // Create the react equivalent for the dropndown
  const GetReactElement = (props) => {
    const containerRef = useRef(null);
    let { columnName, data, isUserDDDisabled } = props;
    let formattedData = data?.split(/[:;]/);
    let userName = formattedData?.[0];
    let email = formattedData?.[1];
    let dropdownContext = {};
    useEffect(() => {
      async function renderDropDown() {
        const userDropdown = context.get('userDropdown');
        const domElement = document.createElement('div');
        if (containerRef.current) {
          containerRef.current.appendChild(domElement);
        }
        dropdownContext = context
          .runner(userDropdown)
          .setParentElement(domElement);
        dropdownContext.set('disabled', isUserDDDisabled);
        try {
          if (userName && email) {
            let userObject = { mail: email, displayName: userName };
            dropdownContext.set('inputUser', userObject);
          }
        } catch (error) {
          getLogger('EditQADialog').error(
            `Failed to get selected user: `,
            error
          );
        }
        // this is triggered by dispatch events from
        dropdownContext.observe('user').subscribe((userObject) => {
          let objectUser = userObject?.displayName
            ?.concat(';')
            ?.concat(userObject?.mail);
          if (columnName === QA_ROLES.CTAG_AG) {
            setCTAG(objectUser);
          } else if (columnName === QA_ROLES.SQA) {
            setSQA(objectUser);
          } else if (columnName === QA_ROLES.QA_IN_FACTORY) {
            setQAFactory(objectUser);
          } else if (columnName === QA_ROLES.BP) {
            setBp(objectUser);
          } else if (columnName === QA_ROLES.PM) {
            setPm(objectUser);
          } else if (columnName === QA_ROLES.PS) {
            setPs(objectUser);
          } else if (columnName === QA_ROLES.REQUESTOR) {
            setRequestr(objectUser);
          }
          getLogger('EditQADialog').info(
            'QA Data latest values ',
            ctag,
            sqa,
            qaInFactory
          );
        });
        return () => {
          containerRef.current.removeChild(domElement);
          dropdownContext.destroy();
          setRefreshMe(false);
        };
      }
      renderDropDown();
    }, []);
    return <div ref={containerRef} />;
  };
  const handleClose = () => {
    setRefreshMe(false);
  };

  useEffect(() => {
    if (dialogRef.current) {
      // Example: remove a specific property
      dialogRef.current.style.removeProperty('display');
    }
  }, []);

  // function to determine if stakeholder dropdown is enabled/disabled
  const getUserDDDisableStatus = (element) => {
    let isDropdownDisabled = false;

    const currentWorkflowAssignee = getCurrentWorkflow(bcpRequest, context)
      ?.get(WorkflowObjectState.assigneeProp)
      .toLowerCase();

    if (currentWorkflowState === BCP_WORKFLOW_STATES.QA_FACTORY_ACCEPTANCE_2) {
      let findWhoApproved;
      // Map for role identification for Admin
      const rolesMap = {
        1: 'Accept as QA in the factory',
        2: 'Accept as SQA',
      };
      // values are identical with QA and SQA column names of dropdownFields array
      const approverMap = {
        1: 'QA in Factory',
        2: 'SQA at host market',
      };

      // Check if the assignee matches `qaInFactoryProp` or `qualityAssuranceProp`
      const qaInFactory = bcpRequest
        .get(BCP.qaInFactoryProp)
        .split(/[;:]/)[1]
        .toLowerCase();
      const qualityAssurance = bcpRequest
        .get(BCP.qualityAssuranceProp)
        .split(/[;:]/)[1]
        .toLowerCase();

      const adminFirstApproval = bcpRequest.get(
        BCP.adminFirstQualityAcceptanceProp
      );
      if (adminFirstApproval && adminFirstApproval === rolesMap[1]) {
        findWhoApproved = approverMap[1]; // admin
      } else if (adminFirstApproval && adminFirstApproval === rolesMap[2]) {
        findWhoApproved = approverMap[2];
      } else {
        if (
          qaInFactory === qualityAssurance &&
          qaInFactory === currentWorkflowAssignee
        ) {
          findWhoApproved = approverMap[1]; // qaInFactory if both match
        } else if (qaInFactory === currentWorkflowAssignee) {
          findWhoApproved = approverMap[1]; // qaInFactory
        } else if (qualityAssurance === currentWorkflowAssignee) {
          findWhoApproved = approverMap[2]; // qualityAssurance
        }
      }
      if (findWhoApproved === element.column) {
        isDropdownDisabled = true;
      }
    }
    if (
      element.workflowStatesForDropdownDisable.includes(currentWorkflowState)
    ) {
      isDropdownDisabled = true;
    }
    return isDropdownDisabled;
  };

  // Set notification stage to Previous state based on BPPmEmail and BuildBCPQAEmail logic
  const setBCPNotificationStage = async () => {
    const t = new Transaction(true);

    if (
      prevValues.current.bp !== bp &&
      currentWorkflowState === BCP_WORKFLOW_STATES.ALT_VS_SUGG_BY_BP
    ) {
      BCP.setNotification_stage(t, bcpRequest, null);
    }
    if (
      prevValues.current.pm !== pm &&
      currentWorkflowState === BCP_WORKFLOW_STATES.ALT_VS_CHECK_BY_PM
    ) {
      BCP.setNotification_stage(t, bcpRequest, null);
    }

    if (
      prevValues.current.ctag !== ctag &&
      currentWorkflowState === BCP_WORKFLOW_STATES.CTAG_ACCEPTANCE
    ) {
      BCP.setNotification_stage(
        t,
        bcpRequest,
        BCP_WORKFLOW_STATES.ALT_VS_CHECK_BY_PM
      );
    }

    if (
      (prevValues.current.sqa !== sqa ||
        prevValues.current.qaInFactory !== qaInFactory) &&
      (currentWorkflowState === BCP_WORKFLOW_STATES.QA_FACTORY_ACCEPTANCE_1 ||
        currentWorkflowState === BCP_WORKFLOW_STATES.QA_FACTORY_ACCEPTANCE_2)
    ) {
      BCP.setNotification_stage(
        t,
        bcpRequest,
        BCP_WORKFLOW_STATES.CTAG_ACCEPTANCE
      );
    }

    // Update previous values for next comparison
    prevValues.current = { ctag, sqa, qaInFactory, bp, pm, ps };
    await t.execute();
  };

  const handleChange = (userObject, columnName) => {
    if (columnName === QA_ROLES.CTAG_AG) {
      setCTAG(userObject);
    } else if (columnName === QA_ROLES.SQA) {
      setSQA(userObject);
    } else if (columnName === QA_ROLES.QA_IN_FACTORY) {
      setQAFactory(userObject);
    } else if (columnName === QA_ROLES.BP) {
      setBp(userObject);
    } else if (columnName === QA_ROLES.PM) {
      setPm(userObject);
    } else if (columnName === QA_ROLES.PS) {
      setPs(userObject);
    } else if (columnName === QA_ROLES.REQUESTOR) {
      setRequestr(userObject);
    }
  };

  return (
    <BootstrapDialog
      ref={dialogRef}
      open={refreshMe}
      onClose={handleClose}
      scroll='paper'
      aria-label='delete-confirmation-dialog'>
      <DialogTitle
        sx={{
          color: '#f19100',
          fontSize: '2rem',
          alignContent: 'center',
          textAlign: 'center',
          lineHeight: '1'
        }}>
        Update Stakeholders
      </DialogTitle>
      <IconButton
        aria-label='close'
        onClick={handleClose}
        sx={(theme) => ({
          position: 'absolute',
          right: 8,
          top: 8,
          color: theme.palette.grey[500],
        })}>
        <CloseIcon />
      </IconButton>
      <Alert icon={<InfoIcon fontSize="inherit" />} severity='info' sx={{ float: 'right', justifyContent: 'right' }}>
      {"Please fill in stakeholder names. If name not found, please input stakeholder email and select the stakeholder. Procurement Specialist is an optional field."}
      </Alert>

      <DialogContent dividers style={{ fontSize: '1.2rem', width: '37.5rem', height: '30rem', paddingTop: '0px' }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={12} lg={12} md={12}>
            {successMessage && <Alert icon={<CheckIcon fontSize="inherit" />} severity="success">
              Stakeholders updated successfully !!
            </Alert>}
          </Grid>
          {dropdownFields.map((element) => {
            return (
              <React.Fragment key={element.column}>
                <Grid
                  item
                  xs={6}
                  sm={6}
                  lg={6}
                  md={6}
                  style={{ marginTop: '10px' }}>
                  {element.label}  
                        <Tooltip title={
                                <React.Fragment>
                                    {/* <Typography color="inherit">Action</Typography><br /> */}
                                    <em>{element.labelText}</em>
                                </React.Fragment>
                            }>
                           {getIcon("Info", true, {
                                style: { minWidth: "25px" },
                            })}</Tooltip>  
                                     </Grid>
                <Grid item xs={6} sm={6} lg={6} md={6}>
                  {/* <GetReactElement
                    columnName={element.column}
                    data={element.value}
                    bcpRequest={bcpRequest}
                    isUserDDDisabled={getUserDDDisableStatus(element)}
                  /> */}
                  <UserDropdown
                    context={context}
                    isUserDDDisabled={getUserDDDisableStatus(element)}
                    handleChange={handleChange}
                    selectedUser={element.value}
                    columnName={element.column}
                  />
                </Grid>
                {element.column === QA_ROLES.SQA && (
                  <a
                    href={sqaLink}
                    target='_blank'
                    className={sqaLink ? 'sqaLink' : 'sqaLinkUndefined'}
                    style={{
                      padding: '15px',
                      paddingBottom: '0px',
                      width: '100%',
                      fontSize: '14px',
                    }}>
                    Refer the link to update SQA
                  </a>
                )}
              </React.Fragment>
            );
          })}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => {
            updateProperty(
              context,
              bcpRequest,
              getProps(QA_ROLES.CTAG_AG),
              ctag || ''
            );
            updateProperty(
              context,
              bcpRequest,
              getProps(QA_ROLES.SQA),
              sqa || ''
            );
            updateProperty(
              context,
              bcpRequest,
              getProps(QA_ROLES.QA_IN_FACTORY),
              qaInFactory || ''
            );

            updateProperty(
              context,
              bcpRequest,
              getProps(QA_ROLES.BP),
              bp || ''
            );

            updateProperty(
              context,
              bcpRequest,
              getProps(QA_ROLES.PM),
              pm || ''
            );

            updateProperty(
              context,
              bcpRequest,
              getProps(QA_ROLES.PS),
              ps || ''
            );
            updateProperty(
              context,
              bcpRequest,
              getProps(QA_ROLES.REQUESTOR),
              requestr || ''
            );
            setSuccessMessage(true)
            setBCPNotificationStage();
            setTimeout(function () {
              closePopup();
            }, 3000);

          }}
          variant='contained'
          size='medium'
          className={'update-QA-apply-changes-button-stakeholder'}
          startIcon={getIcon('Save')}
          disabled={workflowStatesForApplyButtonDisabled.includes(
            currentWorkflowState
          )}>
          Update Stakeholders
        </Button>
      </DialogActions>
    </BootstrapDialog>
  );
};
